import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { AuthProvider } from './context/AuthContext';
import { ContentProtectionProvider, ProtectedContent } from './components/ContentProtection';
import { PreferencesProvider } from './context/PreferencesContext';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';
// Quill editor stylesheet — loaded in the main bundle to prevent a black
// flash when the lazy DocsManagerPage chunk injects it mid-render.
import 'react-quill-new/dist/quill.snow.css';
import HomePage from './pages/HomePage';
import ExamTypePage from './pages/ExamTypePage';
import GradePage from './pages/GradePage';
import MMDPage from './pages/MMDPage';
import DashboardPage from './pages/DashboardPage';
import SubmitQuestionPage from './pages/SubmitQuestionPage';
import ProfilePage from './pages/ProfilePage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import ContentManagementPage from './pages/admin/ContentManagementPage';
import UserManagementPage from './pages/admin/UserManagementPage';
import FinancesPage from './pages/admin/FinancesPage';
import AnalyticsPage from './pages/admin/AnalyticsPage';
import AdminReviewPage from './pages/admin/AdminReviewPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminFinancesPage from './pages/admin/AdminFinancesPage';
import FinanceAnalyticsPage from './pages/admin/FinanceAnalyticsPage';
import FinanceLogsPage from './pages/admin/FinanceLogsPage';
import FinancePlansPage from './pages/admin/FinancePlansPage';
import FinanceSettingsPage from './pages/admin/FinanceSettingsPage';
import AdminRolesPage from './pages/admin/AdminRolesPage';
import AdminFunctionsPage from './pages/admin/AdminFunctionsPage';
import AdminSurveyorsPage from './pages/admin/AdminSurveyorsPage';
import AdminQuestionsPage from './pages/admin/AdminQuestionsPage';
import AdminTopicsPage from './pages/admin/AdminTopicsPage';
import BulkImportQuestionsPage from './pages/admin/BulkImportQuestionsPage';
import BulkImportReviewPage from './pages/admin/BulkImportReviewPage';
import BulkImportAuditPage from './pages/admin/BulkImportAuditPage';
import BulkSimilarityPage from './pages/admin/BulkSimilarityPage';
import AdminPageBuilderPage from './pages/admin/AdminPageBuilderPage';
import AdminUpdatesPage from './pages/admin/AdminUpdatesPage';
import AdminToolsPage from './pages/admin/AdminToolsPage';
import ModuleActivationPage from './pages/admin/ModuleActivationPage';
import AuditLogsPage from './pages/admin/AuditLogsPage';
import SiteSettingsPage from './pages/admin/SiteSettingsPage';
import ActiveSessionsPage from './pages/admin/ActiveSessionsPage';
import HealthMonitoringPage from './pages/admin/HealthMonitoringPage';
import EmbeddingsManagementPage from './pages/admin/EmbeddingsManagementPage';
import AbbreviationDictionaryPage from './pages/admin/AbbreviationDictionaryPage';
import AIServiceConfigPage from './pages/admin/AIServiceConfigPage';
import EmailSettingsPage from './pages/admin/EmailSettingsPage';
import EmailTemplatesPage from './pages/admin/EmailTemplatesPage';
import DataMaintenancePage from './pages/admin/DataMaintenancePage';
import NotificationLogsPage from './pages/admin/NotificationLogsPage';
import ContributionsPage from './pages/admin/ContributionsPage';
import CoinCreditsPage from './pages/admin/CoinCreditsPage';
import CoinRewardsSettingsPage from './pages/admin/CoinRewardsSettingsPage';
import ReviewSettingsPage from './pages/admin/ReviewSettingsPage';
import UserAuditPage from './pages/admin/UserAuditPage';
import AdminClientErrorsPage from './pages/admin/AdminClientErrorsPage';
import AdminDocsPage from './pages/admin/AdminDocsPage';
import DocsManagerPage from './pages/admin/DocsManagerPage';
import AskPage from './pages/AskPage';
import WrittenManagementPage from './pages/admin/WrittenManagementPage';
import MMDManagementPage from './pages/admin/MMDManagementPage';
import PictureManagerPage from './pages/admin/PictureManagerPage';
import NotesManagementPage from './pages/admin/NotesManagementPage';
import FlaggedReportsPage from './pages/admin/FlaggedReportsPage';
import WrittenPaperDetailPage from './pages/admin/WrittenPaperDetailPage';
import LiveClassesPage from './pages/admin/LiveClassesPage';
import LiveClassesUserPage from './pages/LiveClassesUserPage';
import LiveClassRoomPage from './pages/LiveClassRoomPage';
import LiveClassQuestionsPage from './pages/LiveClassQuestionsPage';
import LiveClassArchivePage from './pages/LiveClassArchivePage';
import LiveClassArchiveListPage from './pages/LiveClassArchiveListPage';
import WrittenQuestionBankPage from './pages/WrittenQuestionBankPage';
import WrittenPaperQuestionsPage from './pages/WrittenPaperQuestionsPage';
import WrittenAllQuestionsPage from './pages/WrittenAllQuestionsPage';
import QuestionBankPage from './pages/QuestionBankPage';
import PracticeConfigPage from './pages/PracticeConfigPage';
import PracticeSessionPage from './pages/PracticeSessionPage';
import PracticeResultsPage from './pages/PracticeResultsPage';
import WrittenPracticeSessionPage from './pages/WrittenPracticeSessionPage';
import WrittenPracticeResultsPage from './pages/WrittenPracticeResultsPage';
import ProgressTrackerPage from './pages/ProgressTrackerPage';
import NotebookPage from './pages/NotebookPage';
import InsightsPage from './pages/InsightsPage';
import PlansPage from './pages/user/PlansPage';
import CheckoutPage from './pages/user/CheckoutPage';
import PaymentSuccessPage from './pages/user/PaymentSuccessPage';
import PaymentFailurePage from './pages/user/PaymentFailurePage';
import UpdatesPage from './pages/UpdatesPage';
// Forum pages
import ForumPage from './pages/ForumPage';
import ForumQuestionPage from './pages/ForumQuestionPage';
import ForumNewQuestionPage from './pages/ForumNewQuestionPage';
import ForumEditQuestionPage from './pages/ForumEditQuestionPage';
import ForumManagementPage from './pages/admin/ForumManagementPage';
// Legal and info pages
import { PrivacyPolicyPage, TermsOfUsePage, RefundPolicyPage } from './pages/LegalPage';
import UserGuidePage from './pages/UserGuidePage';
import { GuidelinesPage, GuidelineArticlePage } from './pages/GuidelinesPage';
import HelpHubPage from './pages/HelpHubPage';
import ContactUsPage from './pages/ContactUsPage';
import NotFoundPage from './pages/NotFoundPage';
import MaintenancePage from './pages/MaintenancePage';
import AdminOnlyRoute from './components/common/AdminOnlyRoute';
import { CAPS } from './utils/capabilities';
import AdminRoute from './components/common/AdminRoute';
import AuthenticatedRoute from './components/common/AuthenticatedRoute';
import GoogleAnalytics from './components/GoogleAnalytics';
import MaintenanceBanner from './components/MaintenanceBanner';
import '@/App.css';

// Setup axios interceptor to add auth token to all requests
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors globally + 423 force-change
let sessionExpiredFired = false;  // dedupe — many in-flight requests at expiry

/** Short human-quotable Error ID for 5xx captures, in the same "ERR-XXXX"
 *  shape the ErrorBoundary uses. Callers that render a toast can attach
 *  this to the message; even if no UI shows it, admins can grep the
 *  telemetry collection for the same string. */
const _gen5xxErrorId = () => {
  try {
    const bytes = new Uint8Array(4);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    return 'ERR-' + Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();
  } catch (_e) {
    return 'ERR-' + Date.now().toString(16).slice(-8).toUpperCase();
  }
};

/** Fire-and-forget POST to the telemetry endpoint. Never awaits, never
 *  throws. Used to record 5xx server failures alongside React crashes. */
const _reportServerError = (errorId, error) => {
  try {
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend || typeof fetch === 'undefined') return;
    const status = error.response?.status;
    const method = (error.config?.method || 'GET').toUpperCase();
    const url = error.config?.url || '';
    const body = JSON.stringify({
      error_id: errorId,
      error_type: 'api_5xx',
      message: `${status} on ${method} ${url}: ${String(error.response?.data?.detail || error.message || '').slice(0, 400)}`,
      stack: null,
      component_stack: null,
      url: typeof window !== 'undefined' ? window.location.href : '',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      user_id: localStorage.getItem('user_id'),
      user_role: localStorage.getItem('user_role'),
      timestamp: new Date().toISOString(),
    });
    fetch(`${backend}/api/telemetry/client-error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => { /* swallow */ });
  } catch (_e) { /* swallow — never let telemetry break the error path */ }
};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // 423 Locked = admin forced password reset; user must change password
    // before any other endpoint will work. Surface a global event the
    // AuthContext listens to so it can route to the change-password screen.
    if (error.response?.status === 423) {
      const detail = error.response?.data?.detail;
      const flag = detail?.password_change_required === true || detail?.password_change_required === 'true';
      if (flag) {
        window.dispatchEvent(new CustomEvent('marq:force-password-change'));
      }
    }

    // 401 on a non-auth endpoint = the user's JWT expired (or was invalidated)
    // mid-session. We must actively flip the UI to logged-out so the user
    // doesn't sit in a zombie "looks logged in but every call fails" state.
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isAuthEndpoint =
        url.includes('/auth/new-login') ||
        url.includes('/auth/register') ||
        url.includes('/auth/2fa') ||
        url.includes('/auth/forgot-password') ||
        url.includes('/auth/reset-password');

      // Only act if the user thought they were signed in — otherwise this
      // is just a login attempt with bad credentials.
      const hadToken = !!localStorage.getItem('token');
      if (!isAuthEndpoint && hadToken && !sessionExpiredFired) {
        sessionExpiredFired = true;
        // Clear creds immediately so subsequent requests don't fire 401s too.
        localStorage.removeItem('token');
        localStorage.removeItem('session_id');
        localStorage.removeItem('user_role');
        localStorage.removeItem('user_id');
        delete axios.defaults.headers.common['Authorization'];

        // Let the AuthContext flip user/token state + show the modal/redirect.
        window.dispatchEvent(new CustomEvent('marq:session-expired'));

        // Reset the dedupe flag after the toast/redirect lifecycle so a
        // future expiry (e.g. user signs back in and the new token also
        // expires) still produces feedback.
        setTimeout(() => { sessionExpiredFired = false; }, 5000);
      }
    }

    // 5xx = server-side failure. Passive-log to the telemetry endpoint so
    // admins can grep the DB by Error ID later. We don't attach a UI here
    // (existing per-call handlers already show their own toasts) — we
    // just tag the error object so callers *can* surface the ID if they
    // want. Skip the telemetry endpoint itself to avoid feedback loops.
    if (error.response?.status >= 500 && error.response?.status < 600) {
      const reqUrl = error.config?.url || '';
      if (!reqUrl.includes('/telemetry/client-error')) {
        const errorId = _gen5xxErrorId();
        error.errorId = errorId;
        _reportServerError(errorId, error);
      }
    }

    return Promise.reject(error);
  }
);

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ContentProtectionProvider>
          <PreferencesProvider>
          <BrowserRouter>
          <GoogleAnalytics />
          <MaintenanceBanner />
          <div className="App">
            <Routes>
              {/* ============ PUBLIC ROUTES ============ */}
              <Route path="/" element={<HomePage />} />
              <Route path="/exam-type" element={<Navigate to="/grade" replace />} />
              <Route path="/grade" element={<GradePage />} />
              <Route path="/mmd" element={<MMDPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              {/* iter 110: 5 proprietary surfaces wrapped in
                  <ProtectedContent> — silently blocks copy/select/
                  context-menu on the entire subtree when the site-
                  wide flag is on and viewer is not an admin. */}
              <Route path="/question-bank" element={<ProtectedContent><QuestionBankPage /></ProtectedContent>} />
              <Route path="/written-bank" element={<ProtectedContent><WrittenQuestionBankPage /></ProtectedContent>} />
              <Route path="/written-bank/all" element={<ProtectedContent><WrittenAllQuestionsPage /></ProtectedContent>} />
              <Route path="/written-bank/:paperId" element={<ProtectedContent><WrittenPaperQuestionsPage /></ProtectedContent>} />
              <Route path="/plans" element={<PlansPage />} />
              <Route path="/updates" element={<UpdatesPage />} />
              {/* Legal and info pages */}
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-of-use" element={<TermsOfUsePage />} />
              <Route path="/refund-policy" element={<RefundPolicyPage />} />
              {/* Help hub — chooser page + Docs/Guides with shared shell. */}
              <Route path="/help" element={<HelpHubPage />} />
              <Route path="/help/docs" element={<UserGuidePage />} />
              <Route path="/help/guides" element={<GuidelinesPage />} />
              <Route path="/help/guides/:slug" element={<GuidelineArticlePage />} />
              {/* Old URLs — keep the old routes alive so external
                  links / bookmarks / emails already sent don't 404. The
                  new pages transparently render at the new locations
                  and the old ones just render the same components. */}
              <Route path="/help/guidelines" element={<GuidelinesPage />} />
              <Route path="/help/guidelines/:slug" element={<GuidelineArticlePage />} />
              <Route path="/user-guide" element={<UserGuidePage />} />
              <Route path="/guidelines" element={<GuidelinesPage />} />
              <Route path="/guidelines/:slug" element={<GuidelineArticlePage />} />
              <Route path="/contact-us" element={<ContactUsPage />} />
              <Route path="/maintenance" element={<MaintenancePage />} />

              {/* ============ AUTHENTICATED ROUTES (Logged-in users) ============ */}
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/submit" element={<AuthenticatedRoute><SubmitQuestionPage /></AuthenticatedRoute>} />
              <Route path="/ask" element={<AuthenticatedRoute><AskPage /></AuthenticatedRoute>} />
              <Route path="/practice" element={<AuthenticatedRoute><PracticeConfigPage /></AuthenticatedRoute>} />
              <Route path="/practice/session" element={<AuthenticatedRoute><ProtectedContent><PracticeSessionPage /></ProtectedContent></AuthenticatedRoute>} />
              <Route path="/practice/results" element={<AuthenticatedRoute><ProtectedContent><PracticeResultsPage /></ProtectedContent></AuthenticatedRoute>} />
              <Route path="/practice/written/session" element={<AuthenticatedRoute><ProtectedContent><WrittenPracticeSessionPage /></ProtectedContent></AuthenticatedRoute>} />
              <Route path="/practice/written/results" element={<AuthenticatedRoute><ProtectedContent><WrittenPracticeResultsPage /></ProtectedContent></AuthenticatedRoute>} />
              <Route path="/progress" element={<AuthenticatedRoute><ProgressTrackerPage /></AuthenticatedRoute>} />
              <Route path="/notebook" element={<AuthenticatedRoute><ProtectedContent><NotebookPage /></ProtectedContent></AuthenticatedRoute>} />
              <Route path="/insights" element={<AuthenticatedRoute><InsightsPage /></AuthenticatedRoute>} />
              <Route path="/live-classes" element={<AuthenticatedRoute><LiveClassesUserPage /></AuthenticatedRoute>} />
              <Route path="/live-classes/:classId/join" element={<AuthenticatedRoute><LiveClassRoomPage mode="attendee" /></AuthenticatedRoute>} />
              <Route path="/live-classes/:classId/questions" element={<AuthenticatedRoute><ProtectedContent><LiveClassQuestionsPage /></ProtectedContent></AuthenticatedRoute>} />
              <Route path="/live-classes/:classId/archive" element={<AuthenticatedRoute><ProtectedContent><LiveClassArchivePage /></ProtectedContent></AuthenticatedRoute>} />
              <Route path="/live-classes/archive" element={<AuthenticatedRoute><ProtectedContent><LiveClassArchiveListPage /></ProtectedContent></AuthenticatedRoute>} />
              <Route path="/checkout" element={<AuthenticatedRoute><CheckoutPage /></AuthenticatedRoute>} />
              <Route path="/payment/success" element={<AuthenticatedRoute><PaymentSuccessPage /></AuthenticatedRoute>} />
              <Route path="/payment/failed" element={<AuthenticatedRoute><PaymentFailurePage /></AuthenticatedRoute>} />
              {/* Forum Routes (Paid Feature) */}
              <Route path="/forum" element={<AuthenticatedRoute><ForumPage /></AuthenticatedRoute>} />
              <Route path="/forum/new" element={<AuthenticatedRoute><ForumNewQuestionPage /></AuthenticatedRoute>} />
              <Route path="/forum/edit/:questionId" element={<AuthenticatedRoute><ForumEditQuestionPage /></AuthenticatedRoute>} />
              <Route path="/forum/question/:questionId" element={<AuthenticatedRoute><ForumQuestionPage /></AuthenticatedRoute>} />

              {/* ============ ADMIN + SUB-ADMIN ROUTES ============ */}
              <Route path="/admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
              <Route path="/admin/content" element={<AdminRoute anyCap={[CAPS.MANAGE_CONTENT]}><ContentManagementPage /></AdminRoute>} />
              <Route path="/admin/review" element={<AdminRoute anyCap={[CAPS.MANAGE_CONTENT]}><AdminReviewPage /></AdminRoute>} />
              <Route path="/admin/questions" element={<AdminRoute anyCap={[CAPS.MANAGE_CONTENT]}><AdminQuestionsPage /></AdminRoute>} />
              <Route path="/admin/written" element={<AdminRoute anyCap={[CAPS.MANAGE_CONTENT]}><WrittenManagementPage /></AdminRoute>} />
              <Route path="/admin/written/:paperId" element={<AdminRoute anyCap={[CAPS.MANAGE_CONTENT]}><WrittenPaperDetailPage /></AdminRoute>} />
              <Route path="/admin/picture-manager" element={<AdminRoute anyCap={[CAPS.MANAGE_CONTENT]}><PictureManagerPage /></AdminRoute>} />
              <Route path="/admin/notes/:department" element={<AdminRoute anyCap={[CAPS.MANAGE_CONTENT]}><NotesManagementPage /></AdminRoute>} />
              <Route path="/admin/notes/flagged-reports" element={<AdminRoute anyCap={[CAPS.MANAGE_CONTENT]}><FlaggedReportsPage /></AdminRoute>} />
              <Route path="/admin/forum" element={<AdminRoute anyCap={[CAPS.MANAGE_CONTENT]}><ForumManagementPage /></AdminRoute>} />
              <Route path="/admin/live-classes" element={<AdminRoute anyCap={[CAPS.HOST_CLASSES, CAPS.MANAGE_CLASSES]}><LiveClassesPage /></AdminRoute>} />
              <Route path="/admin/live-classes/:classId/host" element={<AdminRoute anyCap={[CAPS.HOST_CLASSES, CAPS.MANAGE_CLASSES]}><LiveClassRoomPage mode="host" /></AdminRoute>} />

              {/* ============ ADMIN ONLY ROUTES ============ */}
              <Route path="/admin/tools" element={<AdminOnlyRoute allowRoles={['admin','sub_admin']} anyCap={[CAPS.MANAGE_UPDATES, CAPS.GRANT_COINS, CAPS.VIEW_SESSIONS]}><AdminToolsPage /></AdminOnlyRoute>} />
              <Route path="/admin/tools/modules" element={<AdminOnlyRoute><ModuleActivationPage /></AdminOnlyRoute>} />
              <Route path="/admin/tools/audit-logs" element={<AdminOnlyRoute><AuditLogsPage /></AdminOnlyRoute>} />
              <Route path="/admin/tools/settings" element={<AdminOnlyRoute><SiteSettingsPage /></AdminOnlyRoute>} />
              <Route path="/admin/tools/sessions" element={<AdminOnlyRoute allowRoles={['admin','sub_admin']} anyCap={[CAPS.VIEW_SESSIONS]}><ActiveSessionsPage /></AdminOnlyRoute>} />
              <Route path="/admin/tools/health" element={<AdminOnlyRoute><HealthMonitoringPage /></AdminOnlyRoute>} />
              <Route path="/admin/tools/embeddings" element={<AdminOnlyRoute><EmbeddingsManagementPage /></AdminOnlyRoute>} />
              <Route path="/admin/tools/abbreviations" element={<AdminOnlyRoute><AbbreviationDictionaryPage /></AdminOnlyRoute>} />
              <Route path="/admin/tools/ai-service" element={<AdminOnlyRoute><AIServiceConfigPage /></AdminOnlyRoute>} />
              <Route path="/admin/tools/email" element={<AdminOnlyRoute><EmailSettingsPage /></AdminOnlyRoute>} />
              <Route path="/admin/tools/data-maintenance" element={<AdminOnlyRoute><DataMaintenancePage /></AdminOnlyRoute>} />
              <Route path="/admin/tools/notification-logs" element={<AdminOnlyRoute><NotificationLogsPage /></AdminOnlyRoute>} />
              <Route path="/admin/tools/contributions" element={<AdminOnlyRoute><ContributionsPage /></AdminOnlyRoute>} />
              <Route path="/admin/tools/coin-credits" element={<AdminOnlyRoute allowRoles={['admin','sub_admin']} anyCap={[CAPS.GRANT_COINS]}><CoinCreditsPage /></AdminOnlyRoute>} />
              <Route path="/admin/tools/coin-settings" element={<AdminOnlyRoute><CoinRewardsSettingsPage /></AdminOnlyRoute>} />
              <Route path="/admin/tools/review-settings" element={<AdminOnlyRoute><ReviewSettingsPage /></AdminOnlyRoute>} />
              <Route path="/admin/tools/user-activity" element={<AdminOnlyRoute><UserAuditPage /></AdminOnlyRoute>} />
              <Route path="/admin/tools/client-errors" element={<AdminOnlyRoute><AdminClientErrorsPage /></AdminOnlyRoute>} />
              <Route path="/admin/docs" element={<AdminOnlyRoute allowRoles={['admin', 'sub_admin']}><AdminDocsPage /></AdminOnlyRoute>} />
              <Route path="/admin/tools/docs-manager" element={<AdminOnlyRoute><DocsManagerPage /></AdminOnlyRoute>} />
              <Route path="/admin/tools/email/templates" element={<AdminOnlyRoute><EmailTemplatesPage /></AdminOnlyRoute>} />
              <Route path="/admin/user-management" element={<AdminOnlyRoute><UserManagementPage /></AdminOnlyRoute>} />
              <Route path="/admin/users" element={<AdminOnlyRoute allowRoles={['admin','sub_admin']} anyCap={[CAPS.MANAGE_USERS, CAPS.CHANGE_USER_PLAN]}><AdminUsersPage /></AdminOnlyRoute>} />
              <Route path="/admin/roles" element={<AdminOnlyRoute><AdminRolesPage /></AdminOnlyRoute>} />
              <Route path="/admin/finances" element={<AdminOnlyRoute allowRoles={['admin','sub_admin']} anyCap={[CAPS.VIEW_FINANCES]}><AdminFinancesPage /></AdminOnlyRoute>} />
              <Route path="/admin/finances/analytics" element={<AdminOnlyRoute allowRoles={['admin','sub_admin']} anyCap={[CAPS.VIEW_FINANCES]}><FinanceAnalyticsPage /></AdminOnlyRoute>} />
              <Route path="/admin/finances/logs" element={<AdminOnlyRoute allowRoles={['admin','sub_admin']} anyCap={[CAPS.VIEW_FINANCES]}><FinanceLogsPage /></AdminOnlyRoute>} />
              <Route path="/admin/finances/plans" element={<AdminOnlyRoute><FinancePlansPage /></AdminOnlyRoute>} />
              <Route path="/admin/finances/settings" element={<AdminOnlyRoute><FinanceSettingsPage /></AdminOnlyRoute>} />
              <Route path="/admin/analytics" element={<AdminOnlyRoute allowRoles={['admin','sub_admin']} anyCap={[CAPS.VIEW_ANALYTICS]}><AnalyticsPage /></AdminOnlyRoute>} />
              <Route path="/admin/functions" element={<AdminOnlyRoute><AdminFunctionsPage /></AdminOnlyRoute>} />
              <Route path="/admin/surveyors" element={<AdminOnlyRoute><AdminSurveyorsPage /></AdminOnlyRoute>} />
              <Route path="/admin/topics" element={<AdminOnlyRoute><AdminTopicsPage /></AdminOnlyRoute>} />
              <Route path="/admin/bulk-import" element={<AdminOnlyRoute allowRoles={['admin','sub_admin']} anyCap={[CAPS.MANAGE_CONTENT]}><BulkImportQuestionsPage /></AdminOnlyRoute>} />
              <Route path="/admin/bulk-import-review" element={<AdminOnlyRoute allowRoles={['admin','sub_admin']} anyCap={[CAPS.MANAGE_CONTENT]}><BulkImportReviewPage /></AdminOnlyRoute>} />
              <Route path="/admin/bulk-import-audit" element={<AdminOnlyRoute allowRoles={['admin']}><BulkImportAuditPage /></AdminOnlyRoute>} />
              <Route path="/admin/bulk-import-review/batch/:batchId" element={<AdminOnlyRoute allowRoles={['admin','sub_admin']} anyCap={[CAPS.MANAGE_CONTENT]}><BulkSimilarityPage /></AdminOnlyRoute>} />
              <Route path="/admin/page-builder" element={<AdminOnlyRoute><AdminPageBuilderPage /></AdminOnlyRoute>} />
              <Route path="/admin/updates" element={<AdminOnlyRoute allowRoles={['admin','sub_admin']} anyCap={[CAPS.MANAGE_UPDATES]}><AdminUpdatesPage /></AdminOnlyRoute>} />
              <Route path="/admin/mmd" element={<AdminOnlyRoute><MMDManagementPage /></AdminOnlyRoute>} />

              {/* 404 Catch-all route */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            <Toaster position="top-right" richColors />
          </div>
        </BrowserRouter>
      </PreferencesProvider>
      </ContentProtectionProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;